import { Fragment, useState } from 'react'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum } from '../format'
import { useAktion, useLaden } from '../hooks'
import { useSitzung } from '../sitzung'
import { ANLIEGENSTATUS } from '../texte'
import Meldung from './Meldung'
import Meldungen from './Meldungen'
import type { Anliegen, AnliegenStatus } from '../typen'

/**
 * Anliegen an den Entwickler (02.11.2026): statt „das kann ich nicht" legt der Assistent einen Wunsch
 * oder Fehler an, die Plattform schickt ihn per Mail, und hier steht, was daraus wurde - Status und
 * Antwort. Antworten und den Status setzen darf die Verwaltung; alle anderen lesen.
 */
export default function Anliegenliste() {
  const { daten: geholt, fehler: ladeFehler, neuLaden } = useLaden<Anliegen[] | typeof NICHT_VORHANDEN>(() => ohne404(api.anliegen()), [])
  const { benutzer } = useSitzung()
  const verwaltung = benutzer.rolle === 'verwaltung'
  const [bearbeitet, setBearbeitet] = useState<number | null>(null)
  const [status, setStatus] = useState<AnliegenStatus>('offen')
  const [antwort, setAntwort] = useState('')
  const { fehler, erfolg, arbeitet, ausfuehren } = useAktion()
  if (geholt === NICHT_VORHANDEN) return <p className="hinweis">Anliegen kennt das Backend noch nicht.</p>

  function oeffnen(a: Anliegen) {
    setBearbeitet(a.id)
    setStatus(a.status)
    setAntwort(a.antwort ?? '')
  }

  function speichern(id: number) {
    void ausfuehren(async () => {
      await api.anliegenAendern(id, { status, antwort: antwort.trim() || null })
      setBearbeitet(null)
      neuLaden()
    }, 'Anliegen gespeichert.')
  }

  return (
    <div className="karte">
      <div className="leiste">
        <b>Anliegen an den Entwickler</b>
        <span className="hinweis">Wünsche und Fehler, die der Assistent weitergegeben hat - mit dem, was daraus wurde.</span>
      </div>
      {ladeFehler && <Meldung art="fehler">Die Anliegen ließen sich nicht laden: {ladeFehler}</Meldung>}
      <Meldungen fehler={fehler} erfolg={erfolg} />
      <div className="tabellenrahmen">
        <table className="entscheidungen-tabelle">
          <thead>
            <tr>
              <th>Anliegen</th>
              <th>Von, am</th>
              <th>Stand</th>
              <th>Antwort</th>
              {verwaltung && <th></th>}
            </tr>
          </thead>
          <tbody>
            {geholt && geholt.length === 0 && (
              <tr>
                <td colSpan={verwaltung ? 5 : 4} className="leer">
                  Noch keine Anliegen.
                </td>
              </tr>
            )}
            {(geholt ?? []).map((a) => (
              <Fragment key={a.id}>
                <tr className={a.status === 'offen' ? undefined : 'leise'}>
                  <td>
                    <span className="hinweis">{a.art === 'fehler' ? 'Fehler' : 'Wunsch'}:</span> <b>{a.titel}</b>
                    <div>{a.text}</div>
                  </td>
                  <td>
                    {a.angelegt_von ?? ''}
                    <div className="hinweis">{datum(a.angelegt_am)}</div>
                    <div className="hinweis">{a.gemailt_am ? `gemailt ${datum(a.gemailt_am)}` : 'Mail nicht verschickt'}</div>
                  </td>
                  <td>
                    <span className={`status ${ANLIEGENSTATUS[a.status]?.klasse ?? 'offen'}`}>{ANLIEGENSTATUS[a.status]?.text ?? a.status}</span>
                    {a.erledigt_am && <div className="hinweis">{datum(a.erledigt_am)}</div>}
                  </td>
                  <td>{a.antwort ?? <span className="hinweis">noch keine</span>}</td>
                  {verwaltung && (
                    <td>
                      {bearbeitet !== a.id && (
                        <button className="leise" disabled={arbeitet} onClick={() => oeffnen(a)}>
                          Antworten
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {verwaltung && bearbeitet === a.id && (
                  <tr>
                    <td colSpan={5}>
                      <form
                        className="leiste"
                        style={{ marginBottom: 0 }}
                        onSubmit={(e) => {
                          e.preventDefault()
                          speichern(a.id)
                        }}
                      >
                        <label className="feld">
                          <span className="hinweis">Stand</span>
                          <select value={status} onChange={(e) => setStatus(e.target.value as AnliegenStatus)}>
                            {(Object.keys(ANLIEGENSTATUS) as AnliegenStatus[]).map((s) => (
                              <option key={s} value={s}>
                                {ANLIEGENSTATUS[s].text}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="feld anliegen-antwort">
                          <span className="hinweis">Antwort</span>
                          <textarea value={antwort} rows={2} onChange={(e) => setAntwort(e.target.value)} />
                        </label>
                        <button className="haupt" type="submit" disabled={arbeitet}>
                          Speichern
                        </button>
                        <button type="button" disabled={arbeitet} onClick={() => setBearbeitet(null)}>
                          Abbrechen
                        </button>
                      </form>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
